import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import DragItems from '../../../../../../assets/Images/drag.svg';
import {
  ExportIcon,
  PlusIcon,
  RemoveIcon,
} from '../../../../../../shared-components/svgs/svgs';
import { formatMessage } from '../../../../../../translations/format-message';
import { getTenantIdFromLocalStorage } from '../../../../../../helpers/localstorage-helper';
import { useStore } from '../../../../../../contexts/store-provider';
import { useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { IActionFormatter } from '../../../../../../models/ICommon';
import toast from 'react-hot-toast';
import { IProductFiles } from '../../../../../../models/response/IProductResponse';
import ConfirmDialog from '../../../confirm-dialog';

interface IProps {
  id: number;
  selectedImage: IProductFiles[];
  selectedImageIndex: number;
}

const TEPLAR_ROW_SOURCE = {
  existing: 'existing',
  csv: 'csv',
  manual: 'manual',
} as const;

const TaplerView = (props: IProps) => {
  const { teplarPrintStore } = useStore();
  const { values, setFieldValue } = useFormikContext<any>();
  const {
    GetTeplarByIdWithCSVService,
    csvUploadState,
    resetCsvUploadState,
    teplarDetailsList,
    teplarDetailsProductId,
  } = teplarPrintStore;
  const [selectedTeplars, setSelectedTeplars] = useState<any[]>([]);
  const [maxLength, setMaxLength] = useState(0);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const hasInitialized = useRef(false);
  const isCsvImportInProgress = useRef(false);
  const latestTeplarDetailsList = useRef<any[]>([]);
  const csvImportedRowIds = useRef<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteRowIndex, setPendingDeleteRowIndex] = useState<
    number | null
  >(null);

  const getTeplarItems = (item: any) => {
    if (Array.isArray(item?.Items)) return item.Items;
    if (typeof item?.Items === 'string' && item.Items.trim() !== '') {
      return item.Items.split(',');
    }

    return Object.keys(item || {})
      .filter((key) => /^item\s*\d+$/i.test(key))
      .sort((a, b) => {
        const aIndex = Number(a.match(/\d+/)?.[0] || 0);
        const bIndex = Number(b.match(/\d+/)?.[0] || 0);
        return aIndex - bIndex;
      })
      .map((key) => item[key]);
  };

  const chunkArray = (array: string[] | string, size: number) => {
    const sourceArray = Array.isArray(array) ? array : String(array || '').split(',');
    // An empty source means the row has no print-content items typed yet (e.g. a row the
    // user just added, or added a Format for, without filling any columns). Returning []
    // here makes the caller (transformTeplarRows, used to rehydrate the table whenever
    // this component remounts) drop the row entirely instead of preserving it - so a
    // newly-added, still-empty row silently vanishes on any tab/step switch. Return one
    // empty chunk instead so the row survives as an empty-but-present row; the actual
    // save path (normalizeTeplarsForSave in step-four.tsx) already filters truly-blank
    // rows out on submit, so this doesn't cause blank rows to be persisted.
    if (sourceArray.length === 0) return [[]];

    const result: string[][] = [];
    const chunkSize = Math.max(Number(size || 0), 1);
    for (let i = 0; i < sourceArray.length; i += chunkSize) {
      result.push(sourceArray.slice(i, i + chunkSize));
    }
    return result;
  };

  const getNormalizedItems = useCallback((items: string[] | string = '', layout = 0) => {
    const parsedItems = Array.isArray(items)
      ? items
      : items
        ? String(items).split(',')
        : [];
    const normalizedLength = Math.max(Number(layout || 0), parsedItems.length);

    return Array.from({ length: normalizedLength }, (_, index) => {
      const value = parsedItems[index];
      return typeof value === 'string' ? value : '';
    });
  }, []);

  const hasIncompleteVisibleCell = useCallback(
    (teplar: any) =>
      getNormalizedItems(teplar?.Items || '', teplar?.Layout || 0).some(
        (item) => item.trim() === ''
      ),
    [getNormalizedItems]
  );

  const transformTeplarRows = (
    rows: any[] = [],
    withPosition: boolean,
    rowSource: string = TEPLAR_ROW_SOURCE.existing
  ) => {
    // console.log(rows,"rowsrows");

    if (!Array.isArray(rows) || rows.length === 0) return [];

    const columnCount = Math.max(
      ...rows.map((item) => Number(item?.Layout || 0)),
      0
    );

    const teplarDetailsArr: any[] = [];
    rows.forEach((item) => {
      const layout = Number(item?.originalLayout || item?.baseLayout || item?.Layout || 0);
      const effectiveLayout = layout > 0 ? layout : Math.max(columnCount, 1);
      const chunkedItems = chunkArray(getTeplarItems(item), effectiveLayout);
      const transformedChunks = chunkedItems.map((filteredItem, chunkIndex) => {
        const nonEmptyItems = filteredItem
          .map((s: string) => (typeof s === 'string' ? s.trim() : ''))
          .filter((value: string) => value !== '');
        const baseLayoutVal = Math.max(Number(item?.baseLayout || item?.originalLayout || item?.Layout || 0), effectiveLayout, 1);
        const layoutVal = nonEmptyItems.length > 0 ? nonEmptyItems.length : 1;

        return {
          Id: chunkIndex === 0 ? (item?.Id || 0) : 0,
          Format: item?.Format,
          Layout: layoutVal,
          baseLayout: baseLayoutVal,
          Width: item?.Width,
          Items: nonEmptyItems.join(','),
          Position: withPosition ? item?.Position || '' : '',
          Remarks: item?.Remarks || '',
          rowId: item?.rowId || uuidv4(),
          rowSource: item?.rowSource || rowSource,
        };
      });
      teplarDetailsArr.push(...transformedChunks);
    });

    const newMax = teplarDetailsArr.length
      ? Math.max(...teplarDetailsArr.map((t) => t.Layout || 0), 0)
      : 0;
    setMaxLength(newMax);

    return teplarDetailsArr.map((teplar, index) => ({
      ...teplar,
      Order: index + 1,
    }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      isCsvImportInProgress.current = true;
      GetTeplarByIdWithCSVService(
        Number(getTenantIdFromLocalStorage()),
        values.ProductName,
        event.target.files[0],
        props.id > 0,
        values,
        setFieldValue,
        setSelectedTeplars,
        setMaxLength
      );
      event.target.value = null;
    }
  };

  const handleInputChange = (
    rowIndex: number,
    value: string,
    field: string,
    colIndex?: number
  ) => {
    // Mark the product as having user-edited Teplar data so Effect 1 (below) knows this
    // isn't stale server data it should hide on an existing product - it's what the user
    // just typed. Without this, switching process tabs remounts this component, resets
    // the local `selectedTeplars` state, and the remount guard (`props.id > 0 && !IsImport
    // && !hasPersistedProductTeplars`) refuses to restore it since these are brand-new,
    // never-persisted rows. Formik's own ProductTeplars value is unaffected either way,
    // which is why the data still submits correctly despite disappearing from view.
    if (!values?.IsImport) {
      setFieldValue('IsImport', true);
    }
    setSelectedTeplars((prevTeplars) =>
      prevTeplars.map((prevTeplar, index) => {
        if (index !== rowIndex) return prevTeplar;

        if (field === 'Items' && typeof colIndex === 'number') {
          const itemsArray = getNormalizedItems(
            prevTeplar.Items,
            prevTeplar.Layout || 0
          );
          itemsArray[colIndex] = value;

          return {
            ...prevTeplar,
            Items: itemsArray.join(','),
            Layout: Math.max(prevTeplar.Layout || 0, itemsArray.length),
          };
        }

        return { ...prevTeplar, [field]: value };
      })
    );
  };

  const handleTaplerItem = useCallback(
    (rowIndex: number) => {
      const targetRow = selectedTeplars[rowIndex];
      if (!targetRow || hasIncompleteVisibleCell(targetRow)) return;

      const nextLayout =
        Math.max(
          targetRow.Layout || 0,
          getNormalizedItems(targetRow.Items || '', targetRow.Layout || 0)
            .length
        ) + 1;

      setSelectedTeplars((prev) =>
        prev.map((row, idx) =>
          idx === rowIndex
            ? {
              ...row,
              Layout: nextLayout,
              baseLayout: Math.max(row.baseLayout || 0, nextLayout),
            }
            : row
        )
      );

      setMaxLength((prev) => Math.max(prev, nextLayout));
    },
    [selectedTeplars, hasIncompleteVisibleCell, getNormalizedItems]
  );

  const handleRemoveTaplerItem = useCallback(
    (rowIndex: number, colIndex?: number) => {
      setSelectedTeplars((prevTeplars) => {
        const updatedTeplars = prevTeplars.map((teplar, idx) => {
          if (idx !== rowIndex || typeof colIndex !== 'number') return teplar;

          const itemsArray = getNormalizedItems(
            teplar.Items,
            teplar.Layout || 0
          );
          const newItemsArray = itemsArray.slice(0, colIndex);
          const baseLayout = Number(teplar.baseLayout || teplar.originalLayout || teplar.Layout || 1);
          const newLayout = Math.max(0, colIndex);

          return {
            ...teplar,
            Items: newItemsArray.join(','),
            Layout: newLayout,
            baseLayout: baseLayout,
          };
        });

        const newMax = updatedTeplars.length
          ? Math.max(...updatedTeplars.map((t) => t.Layout || 0), 0)
          : 0;
        setMaxLength(newMax);
        return updatedTeplars;
      });
    },
    [getNormalizedItems]
  );

  const handleAddTaplerRow = useCallback(() => {
    // Same reasoning as handleInputChange above: a manually-added row is user data, not
    // stale server data, so the remount guard in Effect 1 needs IsImport to know that.
    if (!values?.IsImport) {
      setFieldValue('IsImport', true);
    }
    setSelectedTeplars((prevTeplars) => {
      const lastRow =
        prevTeplars.length > 0 ? prevTeplars[prevTeplars.length - 1] : null;
      const inheritedLayout = lastRow ? Number(lastRow.Layout ?? 1) : 1;
      const inheritedWidth = lastRow ? Number(lastRow.Width ?? 1) : 1;
      const nextLayout =
        Number.isNaN(inheritedLayout) || inheritedLayout <= 0
          ? 1
          : inheritedLayout;
      const nextWidth =
        Number.isNaN(inheritedWidth) || inheritedWidth <= 0
          ? 1
          : inheritedWidth;

      setMaxLength((prev) => Math.max(prev, nextLayout));

      return [
        ...prevTeplars,
        {
          Id: 0,
          Format: '',
          Layout: nextLayout,
          baseLayout: nextLayout,
          Width: nextWidth,
          Items: '',
          Position: '',
          Remarks: '',
          rowId: uuidv4(),
          rowSource: TEPLAR_ROW_SOURCE.manual,
        },
      ].map((teplar, index) => ({
        ...teplar,
        Order: index + 1,
      }));
    });
  }, [values?.IsImport, setFieldValue]);

  const removeProcessHandler = (index: any) => {
    setPendingDeleteRowIndex(Number(index));
    setShowDeleteConfirm(true);
  };

  const confirmRemoveProcessHandler = () => {
    if (pendingDeleteRowIndex == null) {
      setShowDeleteConfirm(false);
      return;
    }

    setSelectedTeplars((prevTeplars) => {
      const updatedTeplars = withTaplerOrder(
        prevTeplars.filter((_, i) => i !== pendingDeleteRowIndex)
      );

      setMaxLength(
        updatedTeplars.length
          ? Math.max(...updatedTeplars.map((t) => t.Layout || 0), 0)
          : 0
      );

      return updatedTeplars;
    });
    setFieldValue('IsImport', true);
    setPendingDeleteRowIndex(null);
    setShowDeleteConfirm(false);
  };

  const cancelRemoveProcessHandler = () => {
    setPendingDeleteRowIndex(null);
    setShowDeleteConfirm(false);
  };

  const consoleTaplerOrder = (teplars: any[]) => {
    // console.log(
    //   'Tapler template order',
    //   teplars.map((teplar, index) => ({
    //     No: index + 1,
    //     Template: teplar.Format,
    //   }))
    // );
  };

  const withTaplerOrder = (teplars: any[]) =>
    teplars.map((teplar, index) => ({
      ...teplar,
      Order: index + 1,
    }));

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const reorderedTeplars = [...selectedTeplars];
    const [movedTeplar] = reorderedTeplars.splice(result.source.index, 1);
    reorderedTeplars.splice(result.destination.index, 0, movedTeplar);
    const orderedTeplars = withTaplerOrder(reorderedTeplars);

    setSelectedTeplars(orderedTeplars);
    consoleTaplerOrder(orderedTeplars);
  };

  const actionIcons: IActionFormatter[] = [
    {
      dataField: 'delete',
      handler: removeProcessHandler,
      icon: <RemoveIcon />,
      isDisable: false,
      title: 'delete',
      action: 'delete',
      varient: 'danger-new',
    },
  ];

  const visibleColumnCount = useMemo(
    () => {
      const formattedTeplarLayouts = selectedTeplars
        .filter((teplar) => String(teplar?.Format || '').trim() !== '')
        .map((teplar) => Number(teplar?.Layout || 0));

      if (formattedTeplarLayouts.length === 0) return 0;

      return Math.max(maxLength, ...formattedTeplarLayouts, 0);
    },
    [maxLength, selectedTeplars]
  );

  useEffect(() => {
    latestTeplarDetailsList.current = Array.isArray(teplarDetailsList)
      ? teplarDetailsList
      : [];
  }, [teplarDetailsList]);

  const hasPersistedProductTeplars = Array.isArray(values?.ProductTeplars)
    ? values.ProductTeplars.some(
      (teplar: any) => Number(teplar?.Id || 0) > 0
    )
    : false;

  // Effect 1: Initialize from existing product's saved teplars (from API GET response).
  useEffect(() => {
    if (hasInitialized.current) return;
    if (
      !Array.isArray(values?.ProductTeplars) ||
      values.ProductTeplars.length === 0
    )
      return;
    if (props.id > 0 && !values?.IsImport && !hasPersistedProductTeplars)
      return;

    const teplarDetailsArr = transformTeplarRows(values.ProductTeplars, true);
    if (teplarDetailsArr.length > 0) {
      setSelectedTeplars(teplarDetailsArr);
      hasInitialized.current = true;
    }
  }, [values.ProductTeplars, values?.IsImport, hasPersistedProductTeplars, props.id]);

  // Effect 2: Auto-load matching template API rows only while creating a product.
  // Existing products should show template data only after explicit Import.
  useEffect(() => {
    if (hasInitialized.current) return;
    if (isCsvImportInProgress.current) return;
    if (props.id > 0) return;
    // Never auto-load template rows over data the user already has (typed manually, or
    // carried over from a previous step via Formik). This used to rely purely on
    // `hasInitialized.current` being set by Effect 1 first, which only holds if Effect 1
    // always runs to completion before this one on every remount - true in the simple
    // case, but not a guarantee (async store updates, StrictMode double-invoke, etc.).
    // Checking the actual data directly removes that ordering dependency.
    if (Array.isArray(values?.ProductTeplars) && values.ProductTeplars.length > 0)
      return;
    if (
      String(teplarDetailsProductId || '').trim() !==
      String(values?.ProductName || '').trim()
    )
      return;
    if (!Array.isArray(teplarDetailsList) || teplarDetailsList.length === 0)
      return;

    const teplarDetailsArr = transformTeplarRows(
      teplarDetailsList,
      false // new product: no existing positions
    );
    if (teplarDetailsArr.length > 0) {
      setSelectedTeplars(teplarDetailsArr);
      setFieldValue('IsImport', true);
      hasInitialized.current = true;
    }
  }, [
    teplarDetailsList,
    teplarDetailsProductId,
    values?.ProductName,
    values?.ProductTeplars,
    props.id,
    setFieldValue,
  ]);

  useEffect(() => {
    setFieldValue('ProductTeplars', selectedTeplars);
  }, [selectedTeplars, setFieldValue]);

  useEffect(() => {
    if (csvUploadState.success) {
      toast.success(formatMessage('upload_success'));
      // teplarDetailsList is already updated with CSV data at this point (MobX updates it
      // synchronously before setting csvUploadState.success). Directly append to existing rows.
      const latestCsvRows = latestTeplarDetailsList.current;
      if (latestCsvRows.length > 0) {
        const csvRows = transformTeplarRows(
          latestCsvRows,
          props.id > 0,
          TEPLAR_ROW_SOURCE.csv
        );
        setSelectedTeplars((prev) => {
          const isManualRow = (row: any) => {
            if (row.rowSource === TEPLAR_ROW_SOURCE.manual) return true;
            if (!row.rowSource && Number(row.Id || 0) === 0 && !csvImportedRowIds.current.has(row.rowId)) return true;
            return false;
          };

          // Collect manual rows with their original index positions
          const manualPositions = new Map<number, any>();
          prev.forEach((row, idx) => {
            if (isManualRow(row)) {
              manualPositions.set(idx, row);
            }
          });

          let merged: any[];
          if (manualPositions.size === 0) {
            merged = csvRows;
          } else {
            merged = [];
            let csvIdx = 0;
            let currentPos = 0;
            const maxPos = Math.max(
              prev.length,
              manualPositions.size + csvRows.length
            );

            while (csvIdx < csvRows.length || currentPos < maxPos) {
              if (manualPositions.has(currentPos)) {
                merged.push(manualPositions.get(currentPos));
              } else if (csvIdx < csvRows.length) {
                merged.push(csvRows[csvIdx]);
                csvIdx++;
              } else {
                const remainingManualPositions = Array.from(manualPositions.keys()).filter(
                  (pos) => pos > currentPos
                );
                if (remainingManualPositions.length > 0) {
                  const nextManualPos = Math.min(...remainingManualPositions);
                  currentPos = nextManualPos - 1;
                } else {
                  break;
                }
              }
              currentPos++;
            }
          }
          const orderedRows = merged.map((teplar, index) => ({
            ...teplar,
            Order: index + 1,
          }));
          csvImportedRowIds.current = new Set(
            csvRows.map((row) => row.rowId).filter(Boolean)
          );
          setMaxLength(
            orderedRows.length
              ? Math.max(...orderedRows.map((t) => t.Layout || 0), 0)
              : 0
          );
          return orderedRows;
        });
      }
      isCsvImportInProgress.current = false;
      setFieldValue('IsImport', true);
      resetCsvUploadState();
    }
  }, [csvUploadState.success, props.id, resetCsvUploadState, setFieldValue]);

  useEffect(() => {
    if (csvUploadState.error) {
      toast.error(formatMessage(csvUploadState.error));
      isCsvImportInProgress.current = false;
      hasInitialized.current = false;
      resetCsvUploadState();
    }
  }, [csvUploadState.error, resetCsvUploadState]);

  return (
    <>
      {showDeleteConfirm && (
        <ConfirmDialog
          show={showDeleteConfirm}
          title={formatMessage("delete_row")}
          message={formatMessage("message_delete_row_confirm")}
          onCancel={cancelRemoveProcessHandler}
          onConfirm={confirmRemoveProcessHandler}
        />
      )}
      <div className="container1">
        <div className="importPrint product-Import">
          <Button
            type="button"
            className="btn btn-secondary d-flex align-items-center gap-2"
            onClick={handleAddTaplerRow}
          >
            <PlusIcon />
            {formatMessage('add_new_row_teplar')}
          </Button>
          <Button
            data-testid="modalbtn"
            className="btn btn-secondary d-flex align-items-center gap-2 "
            onClick={() => csvInputRef.current?.click()}
          >
            <ExportIcon />
            {formatMessage('import')}
          </Button>
          <input
            type="file"
            accept=".csv"
            ref={csvInputRef}
            className="d-none"
            onChange={handleFileChange}
          />
        </div>
      </div>
      <div className="tableDevices product-img tableDesign">
        <div className="table-responsive">
          <div className="react-bootstrap-table">
            {selectedTeplars && (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="tapler-template-rows">
                  {(provided) => (
                    <table className="table table-hover table-bordered">
                      <thead>
                        <tr>
                          <th className="action-delete"></th>
                          <th>{formatMessage('no')}</th>
                          <th>{formatMessage('template')}</th>
                          {Array.from({ length: visibleColumnCount }).map((_, index) => (
                            <th key={uuidv4()} className="">
                              {formatMessage('print_content')} {index + 1}
                            </th>
                          ))}
                          <th className="sticky-action"></th>
                        </tr>
                      </thead>
                      <tbody
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                      >
                        {selectedTeplars.map((item, index) => {
                          const visibleItems = getNormalizedItems(
                            item.Items || '',
                            item.Layout || 0
                          );
                          const isAddDisabled =
                            visibleItems.length > 0 &&
                            hasIncompleteVisibleCell(item);

                          return (
                            <Draggable
                              key={item.rowId || `tapler-${index}`}
                              draggableId={item.rowId || `tapler-${index}`}
                              index={index}
                            >
                              {(provided) => (
                                <tr
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                >
                                  <td className="">
                                    <div className="d-flex">
                                      {actionIcons.map(
                                        (action: IActionFormatter) => (
                                          <button
                                            className={`btn btn-light-${action.varient} btn-sm`}
                                            key={action.action}
                                            title={formatMessage(action.title)}
                                            type="button"
                                            onClick={() => action.handler(index)}
                                          >
                                            {action.icon}
                                          </button>
                                        )
                                      )}
                                    </div>
                                  </td>
                                  <td>{index + 1}</td>
                                  <td>
                                    <span
                                      {...provided.dragHandleProps}
                                      className="d-inline-flex align-items-center"
                                      style={{ cursor: 'grab' }}
                                    >
                                      <img
                                        src={DragItems}
                                        alt=""
                                        className="drag me-2"
                                      />
                                    </span>
                                    <input
                                      value={item.Format || ''}
                                      type="text"
                                      name="Format"
                                      id="Format"
                                      className="form-control d-inline-block tapler-template-input"
                                      onChange={(e) =>
                                        handleInputChange(
                                          index,
                                          e.target.value,
                                          'Format'
                                        )
                                      }
                                    />
                                  </td>
                                  {Array.from({ length: visibleColumnCount }).map(
                                    (_, colIndex) => {
                                      const shouldRenderInput =
                                        (colIndex < visibleItems.length) && item?.Format;

                                      if (!shouldRenderInput) {
                                        return (
                                          <td
                                            className="position-comment"
                                            key={colIndex}
                                          ></td>
                                        );
                                      }

                                      const value = visibleItems[colIndex] ?? '';
                                      const isLastItem =
                                        colIndex === visibleItems.length - 1;
                                      const isSequentiallyLocked =
                                        colIndex > 0 &&
                                        visibleItems[colIndex - 1]?.trim() === '';

                                      return (
                                        <td
                                          className="position-comment"
                                          key={colIndex}
                                        >
                                          <div
                                            className=""
                                            style={{ position: 'relative' }}
                                          >
                                            <input
                                              value={value}
                                              type="text"
                                              name="Items"
                                              id="Items"
                                              className="form-control"
                                              disabled={isSequentiallyLocked}
                                              onChange={(e) =>
                                                handleInputChange(
                                                  index,
                                                  e.target.value,
                                                  'Items',
                                                  colIndex
                                                )
                                              }
                                            />

                                            {isLastItem && (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  handleRemoveTaplerItem(
                                                    index,
                                                    colIndex
                                                  )
                                                }
                                                className="btn btn-danger btn-sm remove-btn-product"
                                              >
                                                &times;
                                              </button>
                                            )}
                                          </div>
                                        </td>
                                      );
                                    }
                                  )}
                                  <td className="sticky-action">
                                    <button
                                      type="button"
                                      className="btn btn-secondary"
                                      disabled={isAddDisabled}
                                      onClick={() => handleTaplerItem(index)}
                                    >
                                      <span className="me-1">
                                        <PlusIcon />
                                      </span>
                                    </button>
                                  </td>
                                </tr>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </tbody>
                    </table>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TaplerView;
